import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  ClipboardCheck, CheckCircle2, Building2, Loader2, Save, ChevronDown,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function WalkthroughChecklist({ token, properties }) {
  const [selectedProperty, setSelectedProperty] = useState('');
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedCats, setExpandedCats] = useState({});
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadChecklist = async (propId) => {
    if (!propId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND}/api/walkthrough/${propId}`, { headers });
      setChecklist(r.data);
      // Auto-expand all categories
      const cats = {};
      (r.data.items || []).forEach(i => { cats[i.category] = true; });
      setExpandedCats(cats);
    } catch { setChecklist(null); }
    finally { setLoading(false); }
  };

  const toggleItem = (idx) => {
    if (!checklist) return;
    const items = [...checklist.items];
    items[idx] = { ...items[idx], checked: !items[idx].checked };
    const checked = items.filter(i => i.checked).length;
    setChecklist({ ...checklist, items, checked });
  };

  const setNote = (idx, notes) => {
    if (!checklist) return;
    const items = [...checklist.items];
    items[idx] = { ...items[idx], notes };
    setChecklist({ ...checklist, items });
  };

  const save = async () => {
    if (!checklist || !selectedProperty) return;
    setSaving(true);
    try {
      await axios.post(`${BACKEND}/api/walkthrough/${selectedProperty}`, { items: checklist.items }, { headers });
      toast.success('Checklist saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  // Group items by category
  const grouped = useMemo(() => {
    if (!checklist) return [];
    const groups = {};
    checklist.items.forEach((item, idx) => {
      const cat = item.category || 'other';
      if (!groups[cat]) groups[cat] = { label: CAT_LABELS[cat] || cat, items: [] };
      groups[cat].items.push({ ...item, idx });
    });
    return Object.entries(groups);
  }, [checklist]);

  const pct = checklist ? Math.round((checklist.checked || 0) / (checklist.total || 1) * 100) : 0;

  return (
    <div className="pb-20" data-testid="walkthrough-checklist">
      {/* Property selector */}
      <div className="p-4 border-b bg-card/50 space-y-2">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5 text-primary" />
          <h2 className="text-base font-bold">Property Walk-Through</h2>
        </div>
        <Select value={selectedProperty} onValueChange={(v) => { setSelectedProperty(v); loadChecklist(v); }}>
          <SelectTrigger className="h-11"><SelectValue placeholder="Select a property to inspect..." /></SelectTrigger>
          <SelectContent>{(properties || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading && <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>}

      {!loading && !selectedProperty && (
        <div className="py-12 text-center px-4">
          <Building2 className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground">Select a property to generate an inspection checklist.</p>
        </div>
      )}

      {!loading && checklist && (
        <div className="p-4 space-y-3">
          {/* Progress */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full bg-emerald-500 transition-all duration-300" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-sm font-bold text-emerald-600">{pct}%</span>
            <span className="text-xs text-muted-foreground">{checklist.checked || 0}/{checklist.total}</span>
          </div>

          {/* Category groups */}
          {grouped.map(([catKey, cat]) => {
            const isExpanded = expandedCats[catKey] ?? true;
            const catChecked = cat.items.filter(i => i.checked).length;
            return (
              <div key={catKey} className="border rounded-xl overflow-hidden">
                <button onClick={() => setExpandedCats(p => ({ ...p, [catKey]: !isExpanded }))} className="w-full flex items-center gap-2 p-3 bg-card hover:bg-muted/30">
                  <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${!isExpanded ? '-rotate-90' : ''}`} />
                  <span className="text-sm font-semibold flex-1 text-left">{cat.label}</span>
                  <Badge variant="outline" className={`text-[9px] ${catChecked === cat.items.length ? 'bg-emerald-100 text-emerald-700' : ''}`}>
                    {catChecked}/{cat.items.length}
                  </Badge>
                </button>
                {isExpanded && (
                  <div className="border-t divide-y">
                    {cat.items.map(item => (
                      <div key={item.idx} className={`p-3 ${item.checked ? 'bg-emerald-50/50' : ''}`}>
                        <div className="flex items-start gap-3">
                          <Checkbox checked={item.checked} onCheckedChange={() => toggleItem(item.idx)} className="mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <span className={`text-sm ${item.checked ? 'line-through text-muted-foreground' : 'font-medium'}`}>{item.task}</span>
                            {item.product && <span className="text-xs text-muted-foreground ml-1">({item.product})</span>}
                            <Input
                              value={item.notes}
                              onChange={(e) => setNote(item.idx, e.target.value)}
                              placeholder="Add notes..."
                              className="mt-1.5 h-8 text-xs"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Save button - fixed at bottom on mobile */}
          <div className="sticky bottom-16 pt-2">
            <Button onClick={save} disabled={saving} className="w-full h-11 gap-2 shadow-lg">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : pct === 100 ? <CheckCircle2 className="h-4 w-4" /> : <Save className="h-4 w-4" />}
              {pct === 100 ? 'Complete & Save' : 'Save Progress'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const CAT_LABELS = {
  system_check: 'System Checks',
  version_check: 'Version Verification',
  infrastructure: 'Infrastructure',
  security: 'Security',
  other: 'Other',
};
